import { observer } from 'mobx-react-lite';
import { IoIosSearch } from 'react-icons/io';
import ProjectFilter from './ProjectFilter';
import { useEffect, useMemo, useState } from 'react';
import {
    LoadingOutlined,
    DeleteOutlined,
    FolderOpenOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import ProjectCard from '../Globle/ProjectCard';
import { useMainContext } from '../../hooks/useMainContext';
import { Button, message, Modal, Spin } from 'antd';
import { STEP_TYPE } from '../../__utils/UiConst';
import { SERVER_BASE_URL } from '../../__common/env';
import { getIdToken } from '../../utils/getIdToken';

const ProjectSection = observer(() => {
    const { viewManager, uiManager } = useMainContext();
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [openingId, setOpeningId] = useState(null);
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        viewManager.fetchProjects();

        const query = window.location.search;
        const params = new URLSearchParams(query);
        const projectId = params.get('project_id');

        if (projectId) {
            handleOpenProject(projectId);
        }

        // eslint-disable-next-line
    }, []);
    function updateQueryParam(key, value) {
        const url = new URL(window.location.href);
        if (value === null || value === undefined) {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, value);
        }
        window.history.pushState({}, '', url);
    }

    const handleOpenProject = async (projectId, e) => {
        if (e) e.stopPropagation();
        if (loading || openingId === projectId) return;

        setOpeningId(projectId);
        setLoading(true);

        try {
            viewManager.setProjectId(projectId);
            updateQueryParam('project_id', projectId);
            uiManager.setSelectedStep(STEP_TYPE.MAPPING);
            await viewManager.openProject();
        } catch (error) {
            console.error('Error opening project:', error);
            message.error('Failed to open project. Please try again.');
        } finally {
            setLoading(false);
            setOpeningId(null);
        }
    };
    const confirmDelete = (projectId) => {
        Modal.confirm({
            title: 'Delete Project',
            content: 'Are you sure you want to delete this project?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => handleDeleteProject(projectId),
        });
    };

    const handleDeleteProject = async (projectId) => {
        if (!projectId) return;

        setDeletingId(projectId);
        try {
            const token = await getIdToken();
            const response = await fetch(
                `${SERVER_BASE_URL}/project/${projectId}`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
            const data = await response.json();

            if (!response.ok) {
                message.error(data.error);
            } else {
                message.success(data.message);
            }

            await viewManager.fetchProjects();
        } catch (error) {
            console.error('Error deleting project:', error);
            message.error('Error deleting project. Please try again.');
        } finally {
            setDeletingId(null);
        }
    };
    const handleReload = () => {
        viewManager.fetchProjects();
    };
    const filteredProjects = useMemo(() => {
        const searchTerm = searchValue.toLowerCase();
        return viewManager.projects.filter((project) =>
            project.name.toLowerCase().includes(searchTerm),
        );
    }, [viewManager.projects, searchValue]);

    if (viewManager.isLoading || loading) {
        return <Loader />;
    }

    if (viewManager.hasProjectsError) {
        return <ReloadError handleReload={handleReload} />;
    }

    return (
        <div className="collapse-content">
            <SearchBar value={searchValue} onChange={setSearchValue} />
            <ProjectFilter />
            {filteredProjects.length === 0 ? (
                <div className="h-[calc(100vh-292px)] flex flex-col justify-center items-center gap-4">
                    <p className="text-lg text-gray-600">
                        No such projects found. Please create a new project.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-6 gap-[16px] mt-[24px] max-h-[calc(100vh-316px)] overflow-y-auto pr-[8px] pb-[8px]">
                    {filteredProjects.map((project) => (
                        <ProjectCard
                            key={project.id}
                            name={project.name}
                            project={project}
                            handleOpenProject={handleOpenProject}
                            projectId={project.id}
                            confirmDelete={() => {
                                confirmDelete(project.id);
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
});

export default ProjectSection;

const SearchBar = observer(({ value, onChange }) => {
    return (
        <div className="mt-[24px] w-[332px] h-[48px] pl-[16px] overflow-hidden py-[8px] border-[1px] gap-[8px] border-theme-button-bg rounded-[36px] flex items-center">
            <IoIosSearch className="h-[24px] w-[24px] text-theme-stroke" />
            <input
                type="text"
                placeholder="Search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="outline-none text-sm flex-1 pr-2"
            />
        </div>
    );
});

const Loader = () => {
    return (
        <div className="h-[calc(100vh-244px)] flex justify-center items-center">
            <Spin
                indicator={
                    <LoadingOutlined
                        style={{ fontSize: 48, color: '#f46147' }}
                        spin
                    />
                }
            />
        </div>
    );
};

const ReloadError = ({ handleReload }) => {
    return (
        <div className="flex flex-col">
            <div className="h-[calc(100vh-292px)] flex flex-col justify-center items-center gap-4">
                <p className="text-lg text-gray-600">
                    Failed to load projects. Please try again.
                </p>
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleReload}
                    style={{
                        backgroundColor: '#cccccc',
                        color: 'black',
                        border: 'none',
                    }}
                    className="hover:!bg-[#f56147] hover:!text-white transition-colors duration-300">
                    Reload Projects
                </Button>
            </div>
        </div>
    );
};
